import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  LogOut, Hotel, UtensilsCrossed, Sparkles, Plus, Trash2, 
  Receipt, Percent, Calculator, CheckCircle2, AlertTriangle,
  CreditCard, Banknote, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import MultiPaymentInput from './MultiPaymentInput';

export default function CheckoutDialog({ open, onOpenChange, booking, onCheckoutComplete }) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [roomCharges, setRoomCharges] = useState(0);
  const [restaurantOrders, setRestaurantOrders] = useState([]);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percent'
  const [discountReason, setDiscountReason] = useState('');
  const [payments, setPayments] = useState([]); // Multi-payment support
  const [notes, setNotes] = useState('');
  const [guest, setGuest] = useState(null);
  const [room, setRoom] = useState(null);

  // New charge form
  const [newCharge, setNewCharge] = useState({ description: '', amount: '' });
  
  // Manual restaurant/service charge forms with entry type (item or bill)
  const [restaurantEntryType, setRestaurantEntryType] = useState('item'); // 'item' or 'bill'
  const [newRestaurantCharge, setNewRestaurantCharge] = useState({ description: '', amount: '', billNumber: '' });
  
  const [serviceEntryType, setServiceEntryType] = useState('item'); // 'item' or 'bill'
  const [newServiceCharge, setNewServiceCharge] = useState({ description: '', amount: '', billNumber: '' });
  
  // Track manual charges separately
  const [manualRestaurantCharges, setManualRestaurantCharges] = useState([]);
  const [manualServiceCharges, setManualServiceCharges] = useState([]);
  
  // Hotel tax settings
  const [taxSettings, setTaxSettings] = useState({
    room_tax: 12,
    room_service_food_tax: 5,
    room_service_tax: 18,
    restaurant_food_tax: 5,
    restaurant_beverages_tax: 18,
    other_services_tax: 18
  });

  useEffect(() => {
    if (open && booking) {
      loadCheckoutData();
    }
  }, [open, booking]);

  const loadCheckoutData = async () => {
    setLoading(true);
    try {
      // Load all related data in parallel
      const [guestRes, roomRes, ordersRes, servicesRes] = await Promise.all([
        api.get(`/guests/${booking.guest_id}`).catch(() => ({ data: null })),
        api.get(`/rooms/${booking.room_id}`).catch(() => ({ data: null })),
        api.get(`/orders?booking_id=${booking.id}`).catch(() => ({ data: [] })),
        api.get(`/service-orders?booking_id=${booking.id}`).catch(() => ({ data: [] }))
      ]);

      setGuest(guestRes.data);
      setRoom(roomRes.data);
      setRestaurantOrders(ordersRes.data || []);
      setServiceOrders(servicesRes.data || []);
      
      // Calculate room charges
      const nights = booking.total_nights || 1;
      const roomRate = booking.room_rate || (booking.total_amount / 1.18 / nights);
      setRoomCharges(roomRate * nights);
      
      // Set existing discount if any
      setDiscount(booking.discount || 0);
      
      // Load hotel tax settings using the general endpoint
      try {
        const taxRes = await api.get('/saas/hotels/tax-settings');
        if (taxRes.data?.tax_settings) {
          setTaxSettings(taxRes.data.tax_settings);
        }
      } catch (e) {
        console.log('Using default tax settings');
      }
      
    } catch (error) {
      console.error('Error loading checkout data:', error);
      toast.error('Failed to load checkout data');
    } finally {
      setLoading(false);
    }
  };

  const addCharge = () => {
    if (!newCharge.description.trim() || !newCharge.amount) {
      toast.error('Please enter description and amount');
      return;
    }
    setAdditionalCharges([
      ...additionalCharges,
      {
        id: Date.now(),
        description: newCharge.description.trim(),
        amount: parseFloat(newCharge.amount)
      }
    ]);
    setNewCharge({ description: '', amount: '' });
  };

  const removeCharge = (chargeId) => {
    setAdditionalCharges(additionalCharges.filter(c => c.id !== chargeId));
  };

  const removeRestaurantOrder = (orderId) => {
    setRestaurantOrders(restaurantOrders.filter(o => o.id !== orderId));
  };

  const removeServiceOrder = (orderId) => {
    setServiceOrders(serviceOrders.filter(o => o.id !== orderId));
  };

  // Add manual restaurant charge
  const addManualRestaurantCharge = () => {
    if (!newRestaurantCharge.amount) {
      toast.error('Please enter amount');
      return;
    }
    
    if (restaurantEntryType === 'item' && !newRestaurantCharge.description.trim()) {
      toast.error('Please enter item description');
      return;
    }
    
    if (restaurantEntryType === 'bill' && !newRestaurantCharge.billNumber.trim()) {
      toast.error('Please enter bill number');
      return;
    }
    
    const charge = {
      id: `manual-rest-${Date.now()}`,
      entryType: restaurantEntryType,
      description: restaurantEntryType === 'item' 
        ? newRestaurantCharge.description.trim() 
        : `Bill #${newRestaurantCharge.billNumber.trim()}${newRestaurantCharge.description ? ' - ' + newRestaurantCharge.description.trim() : ''}`,
      billNumber: restaurantEntryType === 'bill' ? newRestaurantCharge.billNumber.trim() : null,
      amount: parseFloat(newRestaurantCharge.amount),
      isManual: true
    };
    
    setManualRestaurantCharges([...manualRestaurantCharges, charge]);
    setNewRestaurantCharge({ description: '', amount: '', billNumber: '' });
  };

  const removeManualRestaurantCharge = (chargeId) => {
    setManualRestaurantCharges(manualRestaurantCharges.filter(c => c.id !== chargeId));
  };

  // Add manual service charge
  const addManualServiceCharge = () => {
    if (!newServiceCharge.amount) {
      toast.error('Please enter amount');
      return;
    }
    
    if (serviceEntryType === 'item' && !newServiceCharge.description.trim()) {
      toast.error('Please enter service description');
      return;
    }
    
    if (serviceEntryType === 'bill' && !newServiceCharge.billNumber.trim()) {
      toast.error('Please enter bill number');
      return;
    }
    
    const charge = {
      id: `manual-svc-${Date.now()}`,
      entryType: serviceEntryType,
      description: serviceEntryType === 'item' 
        ? newServiceCharge.description.trim() 
        : `Bill #${newServiceCharge.billNumber.trim()}${newServiceCharge.description ? ' - ' + newServiceCharge.description.trim() : ''}`,
      billNumber: serviceEntryType === 'bill' ? newServiceCharge.billNumber.trim() : null,
      amount: parseFloat(newServiceCharge.amount),
      isManual: true
    };
    
    setManualServiceCharges([...manualServiceCharges, charge]);
    setNewServiceCharge({ description: '', amount: '', billNumber: '' });
  };

  const removeManualServiceCharge = (chargeId) => {
    setManualServiceCharges(manualServiceCharges.filter(c => c.id !== chargeId));
  };

  // Calculate totals with category-specific taxes
  const restaurantOrdersTotal = restaurantOrders.reduce((sum, order) => sum + (order.total_amount || order.total || 0), 0);
  const manualRestaurantTotal = manualRestaurantCharges.reduce((sum, c) => sum + c.amount, 0);
  const restaurantTotal = restaurantOrdersTotal + manualRestaurantTotal;
  
  const serviceOrdersTotal = serviceOrders.reduce((sum, order) => sum + (order.amount || order.total_amount || 0), 0);
  const manualServiceTotal = manualServiceCharges.reduce((sum, c) => sum + c.amount, 0);
  const serviceTotal = serviceOrdersTotal + manualServiceTotal;
  
  const additionalTotal = additionalCharges.reduce((sum, charge) => sum + charge.amount, 0);
  const subtotal = roomCharges + restaurantTotal + serviceTotal + additionalTotal;
  
  const discountAmount = discountType === 'percent' 
    ? (subtotal * discount / 100) 
    : discount;
  
  // Calculate category-specific taxes
  const roomTaxRate = taxSettings.room_tax / 100;
  const restaurantTaxRate = taxSettings.restaurant_food_tax / 100;
  const serviceTaxRate = taxSettings.other_services_tax / 100;
  const additionalTaxRate = taxSettings.other_services_tax / 100;
  
  // Apply discount proportionally to each category
  const discountRatio = subtotal > 0 ? (1 - discountAmount / subtotal) : 1;
  
  const roomTax = roomCharges * discountRatio * roomTaxRate;
  const restaurantTax = restaurantTotal * discountRatio * restaurantTaxRate;
  const serviceTax = serviceTotal * discountRatio * serviceTaxRate;
  const additionalTax = additionalTotal * discountRatio * additionalTaxRate;
  
  const totalTax = roomTax + restaurantTax + serviceTax + additionalTax;
  const taxableAmount = subtotal - discountAmount;
  const grandTotal = taxableAmount + totalTax;
  const amountPaid = booking?.advance_paid || 0;
  const balanceDue = grandTotal - amountPaid;
  
  // Calculate total from payment entries
  const totalPayments = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const isPaymentBalanced = Math.abs(balanceDue - totalPayments) < 0.01;

  const handleCheckout = async () => {
    // Validate payments
    if (balanceDue > 0 && payments.length === 0) {
      toast.error('Please add at least one payment method');
      return;
    }
    
    if (balanceDue > 0 && !isPaymentBalanced) {
      toast.error('Payment amounts must equal the balance due');
      return;
    }

    setProcessing(true);
    try {
      // Format payment transactions for backend
      const paymentTransactions = payments.map(p => ({
        method: p.method,
        amount: parseFloat(p.amount) || 0,
        transaction_id: p.transaction_id || null,
        // Card details
        card_last_four: p.card_last_four || null,
        card_type: p.card_type || null,
        // UPI details
        upi_id: p.upi_id || null,
        // Bank transfer details
        bank_name: p.bank_name || null,
        bank_reference: p.bank_reference || null
      }));

      // Create invoice with all charges
      const invoiceData = {
        booking_id: booking.id,
        guest_id: booking.guest_id,
        room_id: booking.room_id,
        invoice_type: 'checkout',
        room_charges: roomCharges,
        restaurant_charges: restaurantTotal,
        service_charges: serviceTotal,
        additional_charges: additionalCharges,
        manual_restaurant_charges: manualRestaurantCharges,
        manual_service_charges: manualServiceCharges,
        subtotal: subtotal,
        discount: discountAmount,
        discount_reason: discountReason,
        // Itemized taxes
        tax_breakdown: {
          room_tax: { amount: roomTax, rate: taxSettings.room_tax },
          restaurant_tax: { amount: restaurantTax, rate: taxSettings.restaurant_food_tax },
          service_tax: { amount: serviceTax, rate: taxSettings.other_services_tax },
          additional_tax: { amount: additionalTax, rate: taxSettings.other_services_tax }
        },
        tax: totalTax,
        total: grandTotal,
        amount_paid: amountPaid + totalPayments,
        balance_due: Math.max(0, balanceDue - totalPayments),
        payment_transactions: paymentTransactions,
        notes: notes,
        restaurant_orders: restaurantOrders.map(o => o.id),
        service_orders: serviceOrders.map(o => o.id)
      };

      // Create checkout invoice
      await api.post('/invoices/checkout', invoiceData);
      
      // Update booking status to checked_out
      await api.patch(`/bookings/${booking.id}/checkout`);
      
      toast.success('Checkout completed successfully!');
      onCheckoutComplete?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(getErrorMessage(error, 'Failed to complete checkout'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 rounded-full border-4 border-purple-500 border-t-transparent animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col" data-testid="checkout-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-gray-800">
            <LogOut className="h-5 w-5 text-purple-600" />
            Checkout Review
          </DialogTitle>
          <DialogDescription>
            Review all charges before completing checkout for {guest?.name || 'Guest'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
          {/* Left Side - Charges */}
          <ScrollArea className="flex-1 pr-4" style={{ maxHeight: 'calc(95vh - 200px)' }}>
            <div className="space-y-4 pb-4">
              {/* Guest & Room Info */}
              <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Hotel className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800">{guest?.name || 'Guest'}</h3>
                    <p className="text-sm text-gray-500">
                      Room {room?.room_number} • {room?.room_type} • {booking?.total_nights || 1} Night(s)
                    </p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    Checked In
                  </Badge>
                </div>
              </div>

              {/* Room Charges */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 flex items-center gap-2 border-b">
                  <Hotel className="h-4 w-4 text-gray-500" />
                  <h4 className="font-medium text-gray-700">Room Charges</h4>
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-800">Room {room?.room_number} - {room?.room_type}</p>
                      <p className="text-sm text-gray-500">{booking?.total_nights || 1} night(s) × ₹{(roomCharges / (booking?.total_nights || 1)).toFixed(0)}/night</p>
                    </div>
                    <p className="font-bold text-gray-800">₹{roomCharges.toFixed(0)}</p>
                  </div>
                </div>
              </div>

              {/* Restaurant Orders */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-orange-50 px-4 py-2 flex items-center justify-between border-b">
                  <div className="flex items-center gap-2">
                    <UtensilsCrossed className="h-4 w-4 text-orange-500" />
                    <h4 className="font-medium text-orange-700">Restaurant Orders / Charges</h4>
                  </div>
                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                    {restaurantOrders.length + manualRestaurantCharges.length} items
                  </Badge>
                </div>
                <div className="p-4">
                  {restaurantOrders.length === 0 && manualRestaurantCharges.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">No restaurant orders</p>
                  ) : (
                    <div className="space-y-2">
                      {/* Existing restaurant orders */}
                      {restaurantOrders.map((order) => (
                        <div key={order.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium text-gray-800 text-sm">
                              Order #{order.id?.slice(-6)} - {order.order_type}
                            </p>
                            <p className="text-xs text-gray-500">
                              {order.items?.length || 0} items • {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-800">₹{(order.total_amount || order.total || 0).toFixed(0)}</p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-500 hover:text-red-700"
                              onClick={() => removeRestaurantOrder(order.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {/* Manual restaurant charges */}
                      {manualRestaurantCharges.map((charge) => (
                        <div key={charge.id} className="flex justify-between items-center p-2 bg-orange-50 rounded border border-orange-200">
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{charge.description}</p>
                            <p className="text-xs text-orange-600">
                              {charge.entryType === 'bill' ? 'Bill Entry' : 'Item Entry'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-800">₹{charge.amount.toFixed(0)}</p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-500 hover:text-red-700"
                              onClick={() => removeManualRestaurantCharge(charge.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Add manual restaurant charge form */}
                  <div className="pt-3 mt-3 border-t border-orange-200 space-y-2">
                    {/* Entry type toggle */}
                    <div className="flex gap-2 mb-2">
                      <Button
                        size="sm"
                        variant={restaurantEntryType === 'item' ? 'default' : 'outline'}
                        onClick={() => setRestaurantEntryType('item')}
                        className={restaurantEntryType === 'item' ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300 text-orange-600'}
                      >
                        Item-wise
                      </Button>
                      <Button
                        size="sm"
                        variant={restaurantEntryType === 'bill' ? 'default' : 'outline'}
                        onClick={() => setRestaurantEntryType('bill')}
                        className={restaurantEntryType === 'bill' ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300 text-orange-600'}
                      >
                        Bill-wise
                      </Button>
                    </div>
                    
                    {restaurantEntryType === 'item' ? (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Food/Beverage item description"
                          value={newRestaurantCharge.description}
                          onChange={(e) => setNewRestaurantCharge({ ...newRestaurantCharge, description: e.target.value })}
                          className="flex-1 border-orange-200 focus:border-orange-400"
                        />
                        <Input
                          type="number"
                          placeholder="₹ Amount"
                          value={newRestaurantCharge.amount}
                          onChange={(e) => setNewRestaurantCharge({ ...newRestaurantCharge, amount: e.target.value })}
                          className="w-28 border-orange-200 focus:border-orange-400"
                        />
                        <Button size="sm" onClick={addManualRestaurantCharge} className="bg-orange-500 hover:bg-orange-600">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Bill Number"
                          value={newRestaurantCharge.billNumber}
                          onChange={(e) => setNewRestaurantCharge({ ...newRestaurantCharge, billNumber: e.target.value })}
                          className="w-32 border-orange-200 focus:border-orange-400"
                        />
                        <Input
                          placeholder="Description (optional)"
                          value={newRestaurantCharge.description}
                          onChange={(e) => setNewRestaurantCharge({ ...newRestaurantCharge, description: e.target.value })}
                          className="flex-1 border-orange-200 focus:border-orange-400"
                        />
                        <Input
                          type="number"
                          placeholder="₹ Amount"
                          value={newRestaurantCharge.amount}
                          onChange={(e) => setNewRestaurantCharge({ ...newRestaurantCharge, amount: e.target.value })}
                          className="w-28 border-orange-200 focus:border-orange-400"
                        />
                        <Button size="sm" onClick={addManualRestaurantCharge} className="bg-orange-500 hover:bg-orange-600">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Service Orders */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-blue-50 px-4 py-2 flex items-center justify-between border-b">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-blue-500" />
                    <h4 className="font-medium text-blue-700">Service Orders / Charges</h4>
                  </div>
                  <Badge variant="outline" className="text-blue-600 border-blue-300">
                    {serviceOrders.length + manualServiceCharges.length} items
                  </Badge>
                </div>
                <div className="p-4">
                  {serviceOrders.length === 0 && manualServiceCharges.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">No service orders</p>
                  ) : (
                    <div className="space-y-2">
                      {/* Existing service orders */}
                      {serviceOrders.map((service) => (
                        <div key={service.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{service.service_name}</p>
                            <p className="text-xs text-gray-500">{new Date(service.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-800">₹{(service.amount || service.total_amount || 0).toFixed(0)}</p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-500 hover:text-red-700"
                              onClick={() => removeServiceOrder(service.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {/* Manual service charges */}
                      {manualServiceCharges.map((charge) => (
                        <div key={charge.id} className="flex justify-between items-center p-2 bg-blue-50 rounded border border-blue-200">
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{charge.description}</p>
                            <p className="text-xs text-blue-600">
                              {charge.entryType === 'bill' ? 'Bill Entry' : 'Item Entry'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-800">₹{charge.amount.toFixed(0)}</p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-500 hover:text-red-700"
                              onClick={() => removeManualServiceCharge(charge.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Add manual service charge form */}
                  <div className="pt-3 mt-3 border-t border-blue-200 space-y-2">
                    {/* Entry type toggle */}
                    <div className="flex gap-2 mb-2">
                      <Button
                        size="sm"
                        variant={serviceEntryType === 'item' ? 'default' : 'outline'}
                        onClick={() => setServiceEntryType('item')}
                        className={serviceEntryType === 'item' ? 'bg-blue-500 hover:bg-blue-600' : 'border-blue-300 text-blue-600'}
                      >
                        Item-wise
                      </Button>
                      <Button
                        size="sm"
                        variant={serviceEntryType === 'bill' ? 'default' : 'outline'}
                        onClick={() => setServiceEntryType('bill')}
                        className={serviceEntryType === 'bill' ? 'bg-blue-500 hover:bg-blue-600' : 'border-blue-300 text-blue-600'}
                      >
                        Bill-wise
                      </Button>
                    </div>
                    
                    {serviceEntryType === 'item' ? (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Service name (Laundry, Spa, etc.)"
                          value={newServiceCharge.description}
                          onChange={(e) => setNewServiceCharge({ ...newServiceCharge, description: e.target.value })}
                          className="flex-1 border-blue-200 focus:border-blue-400"
                        />
                        <Input
                          type="number"
                          placeholder="₹ Amount"
                          value={newServiceCharge.amount}
                          onChange={(e) => setNewServiceCharge({ ...newServiceCharge, amount: e.target.value })}
                          className="w-28 border-blue-200 focus:border-blue-400"
                        />
                        <Button size="sm" onClick={addManualServiceCharge} className="bg-blue-500 hover:bg-blue-600">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Bill Number"
                          value={newServiceCharge.billNumber}
                          onChange={(e) => setNewServiceCharge({ ...newServiceCharge, billNumber: e.target.value })}
                          className="w-32 border-blue-200 focus:border-blue-400"
                        />
                        <Input
                          placeholder="Description (optional)"
                          value={newServiceCharge.description}
                          onChange={(e) => setNewServiceCharge({ ...newServiceCharge, description: e.target.value })}
                          className="flex-1 border-blue-200 focus:border-blue-400"
                        />
                        <Input
                          type="number"
                          placeholder="₹ Amount"
                          value={newServiceCharge.amount}
                          onChange={(e) => setNewServiceCharge({ ...newServiceCharge, amount: e.target.value })}
                          className="w-28 border-blue-200 focus:border-blue-400"
                        />
                        <Button size="sm" onClick={addManualServiceCharge} className="bg-blue-500 hover:bg-blue-600">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Charges */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-emerald-50 px-4 py-2 flex items-center justify-between border-b">
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4 text-emerald-500" />
                    <h4 className="font-medium text-emerald-700">Additional Charges</h4>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  {additionalCharges.map((charge) => (
                    <div key={charge.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <p className="font-medium text-gray-800 text-sm">{charge.description}</p>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800">₹{charge.amount.toFixed(0)}</p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-500 hover:text-red-700"
                          onClick={() => removeCharge(charge.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Add new charge form */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Input
                      placeholder="Description"
                      value={newCharge.description}
                      onChange={(e) => setNewCharge({ ...newCharge, description: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={newCharge.amount}
                      onChange={(e) => setNewCharge({ ...newCharge, amount: e.target.value })}
                      className="w-28"
                    />
                    <Button size="sm" onClick={addCharge} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Discount */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-red-50 px-4 py-2 flex items-center gap-2 border-b">
                  <Percent className="h-4 w-4 text-red-500" />
                  <h4 className="font-medium text-red-700">Discount</h4>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex gap-2">
                    <Select value={discountType} onValueChange={setDiscountType}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="amount">Amount (₹)</SelectItem>
                        <SelectItem value="percent">Percent (%)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder={discountType === 'percent' ? 'Discount %' : 'Discount ₹'}
                      value={discount}
                      onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                      className="flex-1"
                    />
                  </div>
                  <Input
                    placeholder="Discount reason (optional)"
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-gray-700">Checkout Notes</Label>
                <Textarea
                  placeholder="Any notes for this checkout..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </ScrollArea>

          {/* Right Side - Summary */}
          <div className="w-80 border-l pl-4 flex flex-col overflow-hidden">
            <ScrollArea className="flex-1" style={{ maxHeight: 'calc(95vh - 280px)' }}>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="h-5 w-5 text-gray-600" />
                  <h4 className="font-semibold text-gray-800">Bill Summary</h4>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Room Charges</span>
                    <span className="font-medium">₹{roomCharges.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Restaurant</span>
                    <span className="font-medium">₹{restaurantTotal.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Services</span>
                    <span className="font-medium">₹{serviceTotal.toFixed(0)}</span>
                  </div>
                  {additionalTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Additional</span>
                      <span className="font-medium">₹{additionalTotal.toFixed(0)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between">
                    <span className="text-gray-700 font-medium">Subtotal</span>
                    <span className="font-semibold">₹{subtotal.toFixed(0)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Discount</span>
                      <span>- ₹{discountAmount.toFixed(0)}</span>
                    </div>
                  )}
                  
                  {/* Itemized Tax Breakdown */}
                  <div className="bg-gray-50 rounded-lg p-2 space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Tax Breakdown</p>
                    {roomCharges > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Room Tax ({taxSettings.room_tax}%)</span>
                        <span>₹{roomTax.toFixed(0)}</span>
                      </div>
                    )}
                    {restaurantTotal > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Restaurant Tax ({taxSettings.restaurant_food_tax}%)</span>
                        <span>₹{restaurantTax.toFixed(0)}</span>
                      </div>
                    )}
                    {serviceTotal > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Service Tax ({taxSettings.other_services_tax}%)</span>
                        <span>₹{serviceTax.toFixed(0)}</span>
                      </div>
                    )}
                    {additionalTotal > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Additional Tax ({taxSettings.other_services_tax}%)</span>
                        <span>₹{additionalTax.toFixed(0)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs font-medium border-t border-gray-200 pt-1 mt-1">
                      <span className="text-gray-700">Total Tax</span>
                      <span>₹{totalTax.toFixed(0)}</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-2 flex justify-between text-lg">
                    <span className="font-bold text-gray-800">Grand Total</span>
                    <span className="font-bold text-purple-600">₹{grandTotal.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-600">
                    <span>Advance Paid</span>
                    <span className="font-medium">- ₹{amountPaid.toFixed(0)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between text-lg">
                    <span className="font-bold text-gray-800">Balance Due</span>
                    <span className={`font-bold ${balanceDue > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      ₹{balanceDue.toFixed(0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Multi-Payment Section */}
              {balanceDue > 0 && (
                <div className="mt-4">
                  <MultiPaymentInput
                    totalAmount={balanceDue}
                    payments={payments}
                    onPaymentsChange={setPayments}
                    disabled={processing}
                  />
                </div>
              )}
            </ScrollArea>

            {/* Action Buttons - Fixed at bottom */}
            <div className="pt-4 space-y-2 border-t mt-2 flex-shrink-0">
              {/* Checkout Button */}
              <Button
                onClick={handleCheckout}
                disabled={processing}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12"
              >
                {processing ? (
                  <>
                    <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-5 w-5 mr-2" />
                    Complete Checkout
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
